import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import ReplayIcon from "@mui/icons-material/Replay";
import PageLayout from "../components/navFoot/PageLayout";
import AvatarPlayer from "../components/digitalTwin/AvatarPlayer";
import SlideViewer from "../components/digitalTwin/SlideViewer";
import QAPanel from "../components/digitalTwin/QAPanel";
import { Room, RoomEvent } from "livekit-client";

const KEEP_ALIVE_INTERVAL = 3 * 60 * 1000; // 3 minutes

// LiveAvatar configuration
const LIVEAVATAR_API = "https://api.liveavatar.com/v1";
const LIVEAVATAR_API_KEY = "d1a8a976-64a3-4e8a-926f-02fd75f1b18e";
const AVATAR_ID = "8b355b04-6e42-49b5-807c-c0ce31661879"; // Final custom Avneesh Khosla avatar
const ELEVENLABS_VOICE_ID = "CkHzjaL1cg4JY3n9SsYS"; // Final ElevenLabs voice ID (passed in voice_settings)
// Context ID removed from session creation to prevent its opening_text from auto-playing.
// The scripted phases handle the presentation; Q&A uses conversational mode without preset context.
const IS_SANDBOX = false; // Production mode — uses real credits (2 credits/min)

// ─── Scripted conversation phases (from Revised_chat_script.txt) ───
// Each phase has a scripted line the avatar speaks, then waits for user interaction.
// The presenter (Avneesh on stage) advances phases via the "Next" button.

// NOTE: The brand "Vi" is spelled as "Wee" in all spoken scripts so ElevenLabs TTS
// pronounces it correctly (the brand is pronounced like the English word "we").
const PHASES = [
  {
    id: "intro",
    label: "AI Slide Takeover",
    script:
      "Hi Avneesh! I just saw the word AI. And, that's my domain. So, you can take a chill pill and let me take this from here. Look at the slide, it literally says 'AI led transformation'. That's me. How I have transformed your life. I'm the 'future.' Come on, let me do this one.",
    slidePhase: "AI Slide Takeover",
  },
  {
    id: "present",
    label: "The New Frontier",
    script:
      "I will make you look better than ever. Watch this. At Wee, AI isn't just a tool; it's our engine for a total CX transformation. Wee is deploying a portfolio of specialized AI agents to own the entire customer lifecycle — from discovery to retention. Wee is moving past reactive service. The goal? A predictive, autonomous experience that actually anticipates what our customers need. Today, Wee is prioritizing high-impact agents like our Smart Recharge Assistant and Plan Advisor. These aren't just bots; they analyze usage, location, and budget to provide hyper-personalized recommendations in real-time. Consider the friction in buying a new SIM. Right now, it's overwhelming for consumers — over 80% of users drop off on the first page of our digital journeys because they feel lost. Our AI agents bridge that gap. They act as digital concierges, understanding concerns and guiding users through the finish line. This is only the beginning. In the coming months, Wee will scale these capabilities to unlock new frontiers in marketing efficiency and consumer impact. Stay tuned.",
    slidePhase: "The New Frontier",
  },
  {
    id: "hotseat",
    label: "Hot Seat Q&A",
    script:
      "Wait, wait! Don't just take my word for it — Put me in the hot seat. Let's prove I've got your brain in here.",
    slidePhase: "Live Q&A — Hot Seat",
  },
  {
    id: "qa",
    label: "Live Q&A",
    script: null,
    slidePhase: "Live Q&A",
  },
  {
    id: "closing",
    label: "Closing",
    script:
      "Why don't you ask the audience here how I did? Was I better than the real Avneesh?",
    slidePhase: "Closing",
  },
];

const VIDigitalTwinCMO = () => {
  const isMobile = useMediaQuery("(max-width:900px)");
  const [room, setRoom] = useState(null);
  const [, setSessionId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isAvatarSpeaking, setIsAvatarSpeaking] = useState(false);
  const [isMicActive, setIsMicActive] = useState(false);
  const [transcripts, setTranscripts] = useState([]);
  const [error, setError] = useState(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Phase management
  const [phaseIndex, setPhaseIndex] = useState(-1); // -1 = not started
  const currentPhase = phaseIndex >= 0 ? PHASES[phaseIndex] : null;
  // eslint-disable-next-line no-unused-vars
  const isQAPhase = currentPhase?.id === "qa";

  const fullscreenRef = useRef(null);
  const keepAliveRef = useRef(null);
  const sessionIdRef = useRef(null);
  const roomRef = useRef(null);
  const encoderRef = useRef(new TextEncoder());
  const decoderRef = useRef(new TextDecoder());

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (keepAliveRef.current) clearInterval(keepAliveRef.current);
      stopSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoStartedRef = useRef(false);

  // Fullscreen
  const toggleFullscreen = async () => {
    if (!fullscreenRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await fullscreenRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.warn("[DigitalTwin] Fullscreen error:", err);
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const sendDataChannelEvent = useCallback((eventType, payload = {}) => {
    if (!roomRef.current) return;
    const event = {
      event_id: crypto.randomUUID(),
      event_type: eventType,
      session_id: sessionIdRef.current,
      ...payload,
    };
    const data = encoderRef.current.encode(JSON.stringify(event));
    roomRef.current.localParticipant.publishData(data, {
      reliable: true,
      topic: "agent-control",
    });
  }, []);

  // Auto-start: send the intro as soon as the session connects
  useEffect(() => {
    if (isConnected && sessionActive && phaseIndex === -1 && !autoStartedRef.current) {
      autoStartedRef.current = true;
      // Tell avatar to start listening immediately
      sendDataChannelEvent("avatar.start_listening");
      // Small delay to let the avatar stream stabilize, then speak intro
      const introTimer = setTimeout(() => {
        setPhaseIndex(0);
        sendDataChannelEvent("avatar.speak_text", { text: PHASES[0].script });
      }, 2000);
      return () => clearTimeout(introTimer);
    }
    if (!sessionActive) {
      autoStartedRef.current = false;
    }
  }, [isConnected, sessionActive, phaseIndex, sendDataChannelEvent]);

  const phaseIndexRef = useRef(-1);
  // Keep ref in sync with state so the data handler can read it
  useEffect(() => {
    phaseIndexRef.current = phaseIndex;
  }, [phaseIndex]);

  const handleDataReceived = useCallback(
    (payload, participant, kind, topic) => {
      if (topic !== "agent-response") return;
      try {
        const event = JSON.parse(decoderRef.current.decode(payload));
        switch (event.event_type) {
          case "avatar.speak_started":
            setIsAvatarSpeaking(true);
            break;
          case "avatar.speak_ended":
            setIsAvatarSpeaking(false);
            break;
          case "user.transcription":
            if (event.text) {
              setTranscripts((prev) => [
                ...prev,
                { role: "user", text: event.text },
              ]);
            }
            break;
          case "avatar.transcription":
            if (event.text) {
              setTranscripts((prev) => [
                ...prev,
                { role: "avatar", text: event.text },
              ]);
            }
            break;
          case "session.stopped":
            setSessionActive(false);
            setIsConnected(false);
            break;
          default:
            break;
        }
      } catch (e) {
        console.error("Failed to parse data channel event:", e);
      }
    },
    []
  );

  const startSession = async () => {
    setIsStarting(true);
    setError(null);
    try {
      // Step 1: Create session token
      const tokenPayload = {
        mode: "FULL",
        avatar_id: AVATAR_ID,
        is_sandbox: IS_SANDBOX,
        max_session_duration: 600,
        interactivity_type: "CONVERSATIONAL",
        avatar_persona: {
          language: "en",
          voice_settings: {
            provider: "elevenLabs",
            voice_id: ELEVENLABS_VOICE_ID,
            speed: 0.85,
            stability: 0.75,
            similarity_boost: 0.75,
            style: 0,
            use_speaker_boost: true,
            model: "eleven_flash_v2_5",
          },
          stt_config: {
            provider: "deepgram",
          },
        },
      };

      console.log("[DigitalTwin] Creating session token...", tokenPayload);
      const tokenRes = await fetch(`${LIVEAVATAR_API}/sessions/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-KEY": LIVEAVATAR_API_KEY,
        },
        body: JSON.stringify(tokenPayload),
      });
      const tokenData = await tokenRes.json();
      console.log("[DigitalTwin] Token response:", tokenData);
      if (tokenData.code !== 1000 || !tokenData.data) {
        throw new Error(
          tokenData.message || "Failed to create session token"
        );
      }

      const { session_id, session_token } = tokenData.data;
      setSessionId(session_id);
      sessionIdRef.current = session_id;

      // Step 2: Start session
      console.log("[DigitalTwin] Starting session:", session_id);
      const startRes = await fetch(`${LIVEAVATAR_API}/sessions/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session_token}`,
        },
      });
      const startData = await startRes.json();
      console.log("[DigitalTwin] Start response:", startData);
      if (startData.code !== 1000 || !startData.data) {
        throw new Error(startData.message || "Failed to start session");
      }

      const { livekit_url, livekit_client_token } = startData.data;
      console.log("[DigitalTwin] Connecting to LiveKit:", livekit_url);

      // Step 3: Connect to LiveKit room
      const newRoom = new Room();
      roomRef.current = newRoom;

      newRoom.on(RoomEvent.DataReceived, handleDataReceived);
      newRoom.on(RoomEvent.Connected, () => {
        setIsConnected(true);
        setSessionActive(true);
      });
      newRoom.on(RoomEvent.Disconnected, () => {
        setIsConnected(false);
        setSessionActive(false);
      });

      await newRoom.connect(livekit_url, livekit_client_token);
      console.log("[DigitalTwin] LiveKit connected!");

      // Enable mic from the start so avatar responds immediately to user speech
      await newRoom.localParticipant.setMicrophoneEnabled(true);
      setIsMicActive(true);
      console.log("[DigitalTwin] Session ready (mic on, avatar listening)");

      setRoom(newRoom);

      // Start keep-alive pings
      keepAliveRef.current = setInterval(() => {
        fetch(`${LIVEAVATAR_API}/sessions/keep-alive`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-KEY": LIVEAVATAR_API_KEY,
          },
          body: JSON.stringify({ session_id }),
        }).catch(console.error);
      }, KEEP_ALIVE_INTERVAL);
    } catch (err) {
      console.error("Failed to start session:", err);
      setError(err.message);
    } finally {
      setIsStarting(false);
    }
  };

  const stopSession = async () => {
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }

    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
    }

    const sid = sessionIdRef.current;
    if (sid) {
      try {
        await fetch(`${LIVEAVATAR_API}/sessions/stop`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-KEY": LIVEAVATAR_API_KEY,
          },
          body: JSON.stringify({ session_id: sid, reason: "USER_CLOSED" }),
        });
      } catch (e) {
        console.error("Failed to stop session:", e);
      }
    }

    setRoom(null);
    setSessionId(null);
    sessionIdRef.current = null;
    setIsConnected(false);
    setSessionActive(false);
    setIsAvatarSpeaking(false);
    setPhaseIndex(-1);
  };

  // Advance to next scripted phase
  const advancePhase = () => {
    const nextIdx = phaseIndex + 1;
    if (nextIdx >= PHASES.length) return;

    setPhaseIndex(nextIdx);
    const phase = PHASES[nextIdx];

    if (phase.script) {
      // Send scripted text for avatar to speak
      sendDataChannelEvent("avatar.speak_text", { text: phase.script });
    }

  };

  // Replay the current phase's script
  const replayPhase = () => {
    if (!currentPhase?.script) return;
    sendDataChannelEvent("avatar.speak_text", { text: currentPhase.script });
  };

  const handleSendText = (text) => {
    setTranscripts((prev) => [...prev, { role: "user", text }]);
    sendDataChannelEvent("avatar.speak_response", { text });
  };

  const handleInterrupt = () => {
    sendDataChannelEvent("avatar.interrupt");
    setIsAvatarSpeaking(false);
  };

  const handleToggleMic = async () => {
    if (!roomRef.current) return;
    const newState = !isMicActive;

    await roomRef.current.localParticipant.setMicrophoneEnabled(newState);

    if (newState) {
      sendDataChannelEvent("avatar.start_listening");
    } else {
      sendDataChannelEvent("avatar.stop_listening");
    }

    setIsMicActive(newState);
  };

  // Helper to get the label for the next phase button
  const getNextPhaseLabel = () => {
    // No "Begin" button needed — intro auto-starts on connect
    if (phaseIndex < 0) return null;
    const nextIdx = phaseIndex + 1;
    if (nextIdx >= PHASES.length) return null;
    return PHASES[nextIdx].label;
  };

  const nextLabel = getNextPhaseLabel();

  return (
    <PageLayout>
      <Box
        sx={{
          mt: isMobile ? "60px" : "70px",
          px: { xs: 1.5, md: 3 },
          pb: 1,
          maxWidth: "1400px",
          mx: "auto",
          height: isMobile ? "auto" : "calc(100vh - 70px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 1.5 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fff",
              fontWeight: 700,
              mb: 0.5,
              fontSize: { xs: "1.3rem", md: "1.6rem" },
            }}
          >
            Digital Twin — Avneesh Khosla
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.6)", mb: 1.5 }}
          >
            Chief Marketing Officer, Vi (Vodafone Idea)
          </Typography>

          {/* Session controls */}
          {!sessionActive ? (
            <Button
              variant="contained"
              onClick={startSession}
              disabled={isStarting}
              startIcon={<PlayArrowIcon />}
              sx={{
                backgroundColor: "#7C4DFF",
                borderRadius: "24px",
                px: 4,
                py: 1.2,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "1rem",
                "&:hover": { backgroundColor: "#651FFF" },
                "&:disabled": { backgroundColor: "#444" },
              }}
            >
              {isStarting ? "Starting..." : "Start Session"}
            </Button>
          ) : (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {/* Next phase button */}
              {nextLabel && (
                <Button
                  variant="contained"
                  onClick={advancePhase}
                  disabled={isAvatarSpeaking}
                  startIcon={
                    phaseIndex < 0 ? <PlayArrowIcon /> : <SkipNextIcon />
                  }
                  sx={{
                    backgroundColor: "#7C4DFF",
                    borderRadius: "24px",
                    px: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { backgroundColor: "#651FFF" },
                    "&:disabled": { backgroundColor: "#444" },
                  }}
                >
                  {phaseIndex < 0 ? "Begin Presentation" : `Next: ${nextLabel}`}
                </Button>
              )}

              {/* Replay current phase */}
              {currentPhase?.script && !isAvatarSpeaking && (
                <Button
                  variant="outlined"
                  onClick={replayPhase}
                  startIcon={<ReplayIcon />}
                  sx={{
                    borderColor: "#7C4DFF",
                    color: "#7C4DFF",
                    borderRadius: "24px",
                    px: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#651FFF",
                      backgroundColor: "rgba(124,77,255,0.08)",
                    },
                  }}
                >
                  Replay
                </Button>
              )}

              {/* Current phase chip */}
              {currentPhase && (
                <Chip
                  label={currentPhase.label}
                  sx={{
                    backgroundColor: "rgba(124, 77, 255, 0.15)",
                    color: "#7C4DFF",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                  }}
                />
              )}

              <Button
                variant="outlined"
                onClick={toggleFullscreen}
                startIcon={
                  isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />
                }
                sx={{
                  borderColor: "rgba(255,255,255,0.3)",
                  color: "#fff",
                  borderRadius: "24px",
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#7C4DFF",
                    backgroundColor: "rgba(124,77,255,0.08)",
                  },
                }}
              >
                {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              </Button>

              <Button
                variant="outlined"
                onClick={stopSession}
                startIcon={<StopIcon />}
                sx={{
                  borderColor: "#FF5252",
                  color: "#FF5252",
                  borderRadius: "24px",
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#FF1744",
                    backgroundColor: "rgba(255,82,82,0.08)",
                  },
                }}
              >
                End Session
              </Button>
            </Box>
          )}
        </Box>

        {/* Fullscreen container — takes remaining viewport space */}
        <Box
          ref={fullscreenRef}
          sx={{
            backgroundColor: isFullscreen ? "#0a0e1a" : "transparent",
            borderRadius: isFullscreen ? 0 : "12px",
            p: isFullscreen ? 3 : 0,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            height: isFullscreen ? "100vh" : undefined,
            overflow: "hidden",
          }}
        >
          {/* Fullscreen header bar */}
          {isFullscreen && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#fff", fontWeight: 600 }}
                >
                  Digital Twin — Avneesh Khosla
                </Typography>
                {currentPhase && (
                  <Chip
                    label={currentPhase.label}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(124, 77, 255, 0.15)",
                      color: "#7C4DFF",
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {currentPhase?.script && !isAvatarSpeaking && sessionActive && (
                  <Button
                    variant="outlined"
                    onClick={replayPhase}
                    size="small"
                    startIcon={<ReplayIcon />}
                    sx={{
                      borderColor: "#7C4DFF",
                      color: "#7C4DFF",
                      borderRadius: "20px",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": {
                        borderColor: "#651FFF",
                        backgroundColor: "rgba(124,77,255,0.08)",
                      },
                    }}
                  >
                    Replay
                  </Button>
                )}
                {nextLabel && sessionActive && (
                  <Button
                    variant="contained"
                    onClick={advancePhase}
                    disabled={isAvatarSpeaking}
                    size="small"
                    startIcon={
                      phaseIndex < 0 ? <PlayArrowIcon /> : <SkipNextIcon />
                    }
                    sx={{
                      backgroundColor: "#7C4DFF",
                      borderRadius: "20px",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { backgroundColor: "#651FFF" },
                      "&:disabled": { backgroundColor: "#444" },
                    }}
                  >
                    {phaseIndex < 0 ? "Begin" : `Next: ${nextLabel}`}
                  </Button>
                )}
                <IconButton onClick={toggleFullscreen} sx={{ color: "#fff" }}>
                  <FullscreenExitIcon />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* Main content: Avatar + Slide — fills remaining viewport */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
              flex: 1,
              minHeight: 0,
              mb: 1,
            }}
          >
            {/* Avatar video */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <AvatarPlayer
                room={room}
                isConnected={isConnected}
                isStarting={isStarting}
                sessionActive={sessionActive}
              />
            </Box>

            {/* Single slide — always visible */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <SlideViewer phase={currentPhase?.slidePhase} />
            </Box>
          </Box>

          {/* Q&A Panel — compact, never pushes video out */}
          {sessionActive && (
            <Box sx={{ flexShrink: 0, maxHeight: "180px" }}>
              <QAPanel
                onSendText={handleSendText}
                onInterrupt={handleInterrupt}
                transcripts={transcripts}
                isAvatarSpeaking={isAvatarSpeaking}
                isMicActive={isMicActive}
                onToggleMic={handleToggleMic}
              />
            </Box>
          )}
        </Box>

        {/* Error snackbar */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setError(null)}
            severity="error"
            variant="filled"
          >
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </PageLayout>
  );
};

export default VIDigitalTwinCMO;
